import { FEEDBACK_LIMITS } from '@social-studio/shared';
import { Button, Modal, Notice, Textarea } from '@bennows/boows-react';
import { Send } from 'lucide-react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { sendFeedback, type FeedbackResult } from './feedback-api';
import { toDataUrl, type Mark } from './feedback-draw';
import { FeedbackAnnotator } from './FeedbackAnnotator';

type ErrorKey = 'empty' | 'tooLarge' | Exclude<FeedbackResult, 'sent'>;

interface Props {
  /** The screenshot to mark, or null for a text-only report. */
  readonly image: HTMLCanvasElement | null;
  readonly onClose: () => void;
  readonly onSent: () => void;
}

/**
 * The feedback dialog (spec 05 section 6.1, style guide 2.101): screenshot with marks on the left,
 * description, what is sent along and the send action on the right. Closing with marks or text asks
 * first, inline - no browser confirm.
 */
export function FeedbackDialog({ image, onClose, onSent }: Props) {
  const { t } = useTranslation();
  const [marks, setMarks] = useState<readonly Mark[]>([]);
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ErrorKey | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [attempted, setAttempted] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const changed = marks.length > 0 || message.trim() !== '';

  const requestClose = () => {
    if (busy) return;
    if (confirming) return setConfirming(false);
    if (changed) return setConfirming(true);
    onClose();
  };

  const send = async () => {
    if (busy) return;
    setError(null);
    const description = message.trim();
    if (!description && !image) return setError('empty');
    let data: string | undefined;
    if (image && canvasRef.current) {
      try {
        data = toDataUrl(canvasRef.current);
      } catch {
        return setError('tooLarge');
      }
    }
    setBusy(true);
    const result = await sendFeedback({ message: description, image: data });
    setBusy(false);
    setAttempted(true);
    if (result === 'sent') onSent();
    else setError(result);
  };

  const sendLabel = busy ? t('feedback.dialog.sending') : attempted && error ? t('feedback.dialog.resend') : t('feedback.dialog.send');
  const page = `${decodeURI(window.location.pathname)}${window.location.search}`;

  return (
    <Modal
      open
      onClose={requestClose}
      title={t('feedback.dialog.title')}
      closeLabel={t('feedback.dialog.close')}
      layout={image ? 'wide' : 'compact'}
      width={image ? 'min(1280px, 96vw)' : undefined}
      disableBackdropClose
      className="ui-feedback-form"
      footer={
        <>
          <Button variant="secondary" onClick={requestClose} disabled={busy}>
            {t('feedback.dialog.cancel')}
          </Button>
          <Button variant="primary" loading={busy} iconPrefix={<Send aria-hidden="true" />} onClick={() => void send()}>
            {sendLabel}
          </Button>
        </>
      }
    >
      <div className={`ui-feedback-form__layout${image ? ' has-image' : ''}`}>
        {image ? <FeedbackAnnotator image={image} marks={marks} onMarksChange={setMarks} canvasRef={canvasRef} busy={busy} /> : null}
        <div className="ui-feedback-form__panel">
          <Textarea
            label={t('feedback.dialog.description')}
            value={message}
            onChange={setMessage}
            rows={5}
            maxLength={FEEDBACK_LIMITS.messageMaxLength}
            placeholder={t('feedback.dialog.placeholder')}
            invalid={error === 'empty'}
            disabled={busy}
            autoFocus={!image}
          />
          <dl className="ui-feedback-form__facts" aria-label={t('feedback.dialog.sentAlong')}>
            <dt>{t('feedback.dialog.page')}</dt>
            <dd title={window.location.href}>{page}</dd>
            <dt>{t('feedback.dialog.window')}</dt>
            <dd>{t('feedback.dialog.size', { width: window.innerWidth, height: window.innerHeight })}</dd>
            {image ? (
              <>
                <dt>{t('feedback.dialog.image')}</dt>
                <dd>{t('feedback.dialog.size', { width: image.width, height: image.height })}</dd>
              </>
            ) : null}
          </dl>
          <p className="ui-feedback-form__privacy">{t('feedback.dialog.privacy')}</p>
          {error ? (
            <Notice tone="danger" role="alert">
              {t(`feedback.error.${error}`)}
            </Notice>
          ) : null}
          {confirming ? (
            <Notice
              tone="warning"
              role="alert"
              className="ui-feedback-form__discard"
              actions={
                <>
                  <Button variant="danger" size="sm" onClick={onClose}>
                    {t('feedback.dialog.discard.confirm')}
                  </Button>
                  <Button variant="secondary" size="sm" autoFocus onClick={() => setConfirming(false)}>
                    {t('feedback.dialog.discard.keep')}
                  </Button>
                </>
              }
            >
              {t('feedback.dialog.discard.question')}
            </Notice>
          ) : null}
        </div>
      </div>
    </Modal>
  );
}
